import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import categoryService from '../services/categoryService';

const Categories = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await categoryService.getAdminCategories();
      setCategories(data.categories || []);
    } catch (err) {
      setError('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingCategory) {
        await categoryService.updateCategory(editingCategory._id, formData);
        setSuccess('Category updated successfully!');
      } else {
        await categoryService.createCategory(formData);
        setSuccess('Category created successfully!');
      }
      
      setShowModal(false);
      setFormData({ name: '', description: '', isActive: true });
      setEditingCategory(null);
      fetchCategories();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      isActive: category.isActive
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    try {
      await categoryService.deleteCategory(id);
      setSuccess('Category deleted successfully!');
      fetchCategories();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete category');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setFormData({ name: '', description: '', isActive: true });
    setError('');
  };

  return (
    <>
      <Sidebar />
      <div className="dashboard">
        <Navbar />
        <div className="container" style={{ maxWidth: '100%', padding: '32px' }}>
        
        <div className="page-header" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '32px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700' }}>📁 Categories Management</h1>
          <button 
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
            style={{ width: 'auto', padding: '12px 24px' }}
          >
            + Add Category
          </button>
        </div>

          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          {loading ? (
            <div className="loading">
              <div className="loading-spinner"></div>
              <p>Loading categories...</p>
            </div>
          ) : categories.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📁</div>
              <h3>No Categories Yet</h3>
              <p>Create your first category to start organizing your videos!</p>
              <button 
                className="btn btn-primary"
                onClick={() => setShowModal(true)}
                style={{ width: 'auto' }}
              >
                + Create Category
              </button>
            </div>
          ) : (
            <div style={{ 
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              overflow: 'hidden',
              width: '100%'
            }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ 
                  width: '100%',
                  borderCollapse: 'collapse',
                  minWidth: '800px'
                }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ 
                        padding: '16px 24px',
                        textAlign: 'left',
                        fontWeight: '600',
                        fontSize: '14px',
                        color: '#475569',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>Name</th>
                      <th style={{ 
                        padding: '16px 24px',
                        textAlign: 'left',
                        fontWeight: '600',
                        fontSize: '14px',
                        color: '#475569',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>Description</th>
                      <th style={{ 
                        padding: '16px 24px',
                        textAlign: 'center',
                        fontWeight: '600',
                        fontSize: '14px',
                        color: '#475569',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>Status</th>
                      <th style={{ 
                        padding: '16px 24px',
                        textAlign: 'center',
                        fontWeight: '600',
                        fontSize: '14px',
                        color: '#475569',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((category, index) => (
                      <tr key={category._id} style={{ 
                        borderBottom: index < categories.length - 1 ? '1px solid #e2e8f0' : 'none',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'white'}>
                        <td style={{ padding: '20px 24px' }}>
                          <strong style={{ fontSize: '15px', color: '#1e293b' }}>{category.name}</strong>
                        </td>
                        <td style={{ padding: '20px 24px', color: '#64748b', fontSize: '14px' }}>
                          {category.description || <em style={{ color: '#cbd5e1' }}>No description</em>}
                        </td>
                        <td style={{ padding: '20px 24px', textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '6px 12px',
                            borderRadius: '20px',
                            fontSize: '13px',
                            fontWeight: '500',
                            background: category.isActive ? '#dcfce7' : '#fee2e2',
                            color: category.isActive ? '#166534' : '#991b1b'
                          }}>
                            {category.isActive ? '✓ Active' : '✕ Inactive'}
                          </span>
                        </td>
                        <td style={{ padding: '20px 24px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => handleEdit(category)}
                              className="btn btn-primary"
                              style={{
                                padding: '8px 16px',
                                width: 'auto',
                                fontSize: '13px',
                                minWidth: '80px'
                              }}
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => handleDelete(category._id)}
                              className="btn btn-danger"
                              style={{
                                padding: '8px 16px',
                                width: 'auto',
                                fontSize: '13px',
                                minWidth: '80px'
                              }}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Modal */}
          {showModal && (
            <div className="modal-overlay" onClick={handleCloseModal} style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px'
            }}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{
                background: 'white',
                borderRadius: '16px',
                padding: '32px',
                maxWidth: '500px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
              }}>
                <div>
                <h2 style={{ marginBottom: '24px', fontSize: '24px', fontWeight: '700', color: '#1e293b' }}>
                  {editingCategory ? '✏️ Edit Category' : '➕ Add Category'}
                </h2>
              
              {error && <div className="alert alert-error">{error}</div>}

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Category Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Enter category name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter category description (optional)"
                    rows="3"
                  />
                </div>

                <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                  <button type="submit" className="btn btn-primary">
                    {editingCategory ? 'Update' : 'Create'}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </button>
                </div>
              </form>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Categories;
